import React from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { FormAccordion, FormAccordionItem } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { FormTitle } from 'src/financial/financial-data/financial-item/forms/common/FormTitle';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import { useJointOwnerDefault } from 'src/financial/financial-data/financial-item/forms/common/useJointOwnerDefault';
import {
  OWNER,
  OwnerSharesSection,
  resolveOwnersPayload,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  FEE,
  MortgageFeeFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/MortgageFeeFormAccordionItemList';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';

const MORTGAGE_PRODUCT = 'Mortgage';

export function Mortgage(props: ProductFormComponentProps) {
  const { onSave } = props;
  const { flagEnabledFinancialItemOwners } = useFlags();

  const { isCreateMode, uiData, initialValues, onDeleteConfirm, feedInfo, onConnectAsset } =
    getFinancialFormSetupValues(MORTGAGE_PRODUCT, props, [
      { product: FEE, field: 'fees' },
      { product: OWNER, field: 'owners' },
    ]);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
  });

  useJointOwnerDefault(form, MORTGAGE_PRODUCT, isCreateMode);

  const handleSave = async (values: FormValuesType) => {
    if (FEE in values && Array.isArray(values[FEE])) {
      const ownersPayload = resolveOwnersPayload(
        values,
        MORTGAGE_PRODUCT,
        flagEnabledFinancialItemOwners,
      );
      if (ownersPayload === null) return;

      const mappedValues = {
        ...values[MORTGAGE_PRODUCT],
        fees: values[FEE].map(item => ({ ...item })),
        ...ownersPayload,
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[MORTGAGE_PRODUCT]);
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete={'off'}>
        <FormContent
          form={form}
          feedInfo={feedInfo}
          onConnect={onConnectAsset}
          showOwners={flagEnabledFinancialItemOwners}
        />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
  showOwners: boolean;
};

const FormContent = ({ form, feedInfo, onConnect, showOwners }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  return (
    <FinancialFormContainer
      title={<FormTitle title={'Liabilities > Mortgage'} />}
      feedInfo={feedInfo}
      onConnect={onConnect}
    >
      <FormAccordion defaultValue={['summary', 'total', 'breakdown', 'protected', 'fee']}>
        <FormAccordionItem value="summary" label="Summary">
          <Grid>
            <GridPair
              first={<MortgageField field="type" />}
              second={<MortgageField field="description" />}
            />
            <GridPair
              first={<MortgageField field="provider" />}
              second={<MortgageField field="provider_reference_number" />}
            />
            <GridPair
              first={<MortgageField field="status" />}
              second={showOwners ? undefined : <MortgageField field="is_joint" />}
            />
            <GridPair
              isHighlighted
              first={<MortgageField field="remaining_balance" />}
              second={<MortgageField field="remaining_balance_date" />}
            />
            {showOwners && (
              <GridPair
                fullWidth
                first={
                  <OwnerSharesSection product={MORTGAGE_PRODUCT} valueField="remaining_balance" />
                }
              />
            )}
            <GridPair first={<MortgageField field="is_serviced_by_advisor" />} />
          </Grid>
        </FormAccordionItem>

        <FormAccordionItem value="total" label="Repayment information">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="repayment_amount" />}
              second={<MortgageField field="repayment_frequency" />}
            />
            <GridPair first={<MortgageField field="repayment_type" />} />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Interest rate details">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="interest_rate_type" />}
              second={<MortgageField field="interest_rate" />}
            />
            <GridPair
              isHighlighted
              first={<MortgageField field="rate_period_start" />}
              second={<MortgageField field="rate_period_end" />}
            />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Initial loan amount and term">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="initial_amount" />}
              second={<MortgageField field="term_start_date" />}
            />
            <GridPair first={<MortgageField field="term_end_date" />} />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Charges">
          <Grid>
            <GridPair
              first={<MortgageField field="early_payment_charge" />}
              second={<MortgageField field="redemption_fee_amount" />}
            />
          </Grid>
        </FormAccordionItem>
        <MortgageFeeFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};

type DefinedBenefitPensionFieldProps = {
  field: string;
};

const MortgageField = ({ field }: DefinedBenefitPensionFieldProps) => (
  <FinancialFormField field={field} product={MORTGAGE_PRODUCT} />
);
